import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    // Bypass ngrok free-tier browser interstitial on dev tunnels
    'ngrok-skip-browser-warning': 'true',
  },
  withCredentials: true, // Enable sending cookies for secure httpOnly token storage
});

// Request interceptor to add auth token (fallback for environments where cookies don't work)
api.interceptors.request.use(
  (config) => {
    // Only add Authorization header if no cookie is being sent
    // This provides backward compatibility while preferring cookies
    const token = localStorage.getItem('token');
    if (token && !document.cookie.includes('token=')) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Custom event for auth failure (allows SPA-friendly navigation)
export const AUTH_LOGOUT_EVENT = 'auth:logout';

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Dispatch custom event instead of hard redirect to preserve SPA history
      window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
};

// Customer API
export const customerAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getAllForDropdown: () => api.get('/customers/all'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  bulkDelete: (ids) => api.post('/customers/bulk-delete', { ids }),
  duplicate: (id) => api.post(`/customers/${id}/duplicate`),
  getQuotations: (id) => api.get(`/customers/${id}/quotations`),
  export: () => api.get('/customers/export/csv', { responseType: 'blob' }),
  import: (data) => api.post('/customers/import', { data }),
};

// Product API
export const productAPI = {
  getAll: (params) => api.get('/products', { params }),
  getAllForDropdown: () => api.get('/products/all'),
  getCategories: () => api.get('/products/categories'),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  bulkDelete: (ids) => api.post('/products/bulk-delete', { ids }),
  duplicate: (id) => api.post(`/products/${id}/duplicate`),
  bulkCreate: (products) => api.post('/products/bulk', { products }),
};

// Quotation API
export const quotationAPI = {
  getAll: (params) => api.get('/quotations', { params }),
  getStats: () => api.get('/quotations/stats'),
  getLocations: () => api.get('/quotations/locations'),
  getById: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  updateStatus: (id, status, notes, rejectionReason) => 
    api.put(`/quotations/${id}/status`, { status, notes, rejectionReason }),
  duplicate: (id) => api.post(`/quotations/${id}/duplicate`),
  createRevision: (id, data) => api.post(`/quotations/${id}/revision`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  bulkDelete: (ids) => api.post('/quotations/bulk-delete', { ids }),
  import: (data) => api.post('/quotations/import', { data }),
  export: () => api.get('/quotations/export', { responseType: 'blob' }),
  downloadPDF: (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' }),
  previewPDF: (data) => api.post('/quotations/preview-pdf', data, { responseType: 'blob' }),
  previewEmail: (id, params) => api.get(`/quotations/${id}/email-preview`, { params }),
  generateNumber: () => api.get('/quotations/generate-number'),
  convertToOrder: (id) => api.post(`/quotations/${id}/convert-to-order`),
  sendEmail: (id, data) => api.post(`/quotations/${id}/send-email`, data),
  sign: (id, data) => api.post(`/quotations/${id}/sign`, data),
  getPublic: (token) => api.get(`/quotations/public/${token}`),
  // Attachments
  getAttachments: (id) => api.get(`/quotations/${id}/attachments`),
  uploadAttachments: (id, formData, onUploadProgress) =>
    api.post(`/quotations/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),
  deleteAttachment: (id, attachmentId) =>
    api.delete(`/quotations/${id}/attachments/${attachmentId}`),
};

// Quotation Template API
export const templateAPI = {
  getAll: (params) => api.get('/templates', { params }),
  getAllForDropdown: () => api.get('/templates/all'),
  getById: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  use: (id) => api.post(`/templates/${id}/use`),
};

// Sales Order API
export const salesOrderAPI = {
  getAll: (params) => api.get('/sales-orders', { params }),
  getStats: () => api.get('/sales-orders/stats'),
  getById: (id) => api.get(`/sales-orders/${id}`),
  create: (data) => api.post('/sales-orders', data),
  createFromQuotation: (quotationId) => api.post(`/sales-orders/from-quotation/${quotationId}`),
  update: (id, data) => api.put(`/sales-orders/${id}`, data),
  updateStatus: (id, status, notes) => api.put(`/sales-orders/${id}/status`, { status, notes }),
  delete: (id) => api.delete(`/sales-orders/${id}`),
};

// Invoice API
export const invoiceAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getStats: () => api.get('/invoices/stats'),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  createFromOrder: (orderId) => api.post(`/invoices/from-order/${orderId}`),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  recordPayment: (id, data) => api.post(`/invoices/${id}/payment`, data),
  send: (id, emailData) => emailData 
    ? api.post(`/invoices/${id}/send-email`, emailData)
    : api.put(`/invoices/${id}/send`),
  sendEmail: (id, data) => api.post(`/invoices/${id}/send-email`, data),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/invoices/${id}`),
};

// Activity API
export const activityAPI = {
  getAll: (params) => api.get('/activities', { params }),
  getToday: () => api.get('/activities/today'),
  getUpcoming: () => api.get('/activities/upcoming'),
  getOverdue: () => api.get('/activities/overdue'),
  getStats: () => api.get('/activities/stats'),
  getById: (id) => api.get(`/activities/${id}`),
  create: (data) => api.post('/activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  complete: (id, notes) => api.put(`/activities/${id}/complete`, { notes }),
  delete: (id) => api.delete(`/activities/${id}`),
};

// Contact API
export const contactAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getAllForDropdown: () => api.get('/contacts/all'),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  toggleStar: (id) => api.put(`/contacts/${id}/star`),
  bulkImport: (contacts) => api.post('/contacts/bulk', { contacts }),
  getCountries: () => api.get('/contacts/meta/countries'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard'),
};

// Payment Terms API
export const paymentTermAPI = {
  getAll: () => api.get('/payment-terms'),
  getAllForDropdown: () => api.get('/payment-terms/dropdown'),
  create: (data) => api.post('/payment-terms', data),
  update: (id, data) => api.put(`/payment-terms/${id}`, data),
  delete: (id) => api.delete(`/payment-terms/${id}`),
};

// User/Salesperson API
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getSalespersons: () => api.get('/users/salespersons'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Activity Log API (for change tracking and notes)
export const activityLogAPI = {
  // Get activity logs for a document
  getByDocument: (documentType, documentId, params = {}) => 
    api.get('/activity-logs', { params: { documentType, documentId, ...params } }),
  // Add a manual note
  addNote: (documentType, documentId, content, documentNumber = '') => 
    api.post('/activity-logs/note', { documentType, documentId, documentNumber, content }),
  // Send a message
  addMessage: (documentType, documentId, content, documentNumber = '') => 
    api.post('/activity-logs/message', { documentType, documentId, documentNumber, content }),
  // Get recent activity logs (for dashboard)
  getRecent: (limit = 20) => api.get('/activity-logs/recent', { params: { limit } }),
};

export default api;
